import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import zxcvbn from "zxcvbn";

import AuthLayout, { authField, AuthLabel } from "../../components/auth/AuthLayout";
import { signupAPI } from "../../../services/authService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { paths } from "../../../routes/paths";
import tokens from "../../theme/tokens";

const { color, font, radius } = tokens;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\d{10}$/;

// zxcvbn scores 0–4.
const STRENGTH = [
  { label: "Very weak", color: color.danger },
  { label: "Weak", color: color.danger },
  { label: "Fair", color: color.warning },
  { label: "Strong", color: color.success },
  { label: "Very strong", color: color.success },
];

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [data, setData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
  });
  const [strength, setStrength] = useState(0);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "", form: "" }));
    if (name === "password") setStrength(value ? zxcvbn(value).score : 0);
  };

  const validate = () => {
    const next = {};
    if (!data.first_name.trim()) next.first_name = "Required";
    if (!data.last_name.trim()) next.last_name = "Required";
    if (!EMAIL.test(data.email)) next.email = "Enter a valid email address.";
    // Phone is optional, but must be 10 digits if given.
    if (data.phone && !PHONE.test(data.phone)) next.phone = "Enter 10 digits.";
    if (!data.password) next.password = "Password is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const result = await signupAPI({
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        password: data.password,
      });

      if (result.error) {
        setErrors({ form: result.errorMessage });
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: result.errorMessage,
          })
        );
        return;
      }

      dispatch(
        showAppAlert({
          show: true,
          alertType: "success",
          alertMessage: "Account created — you can sign in now.",
        })
      );
      navigate(paths.v1.signin, {
        state: { email: data.email, password: data.password },
      });
    } catch (err) {
      console.error("Error during signup:", err);
      setErrors({ form: "An error occurred. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const meter = STRENGTH[strength];

  return (
    <AuthLayout
      title="Create an account"
      subtitle="Start screening candidates in minutes."
      footer={
        <>
          Already have an account?{" "}
          <Box
            component={Link}
            to={paths.v1.signin}
            sx={{
              font: `700 12.5px ${font.sans}`,
              color: color.indigo,
              textDecoration: "none",
            }}
          >
            Sign in
          </Box>
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 1.75 }}>
          <Box>
            <AuthLabel>First name</AuthLabel>
            <TextField
              fullWidth
              name="first_name"
              value={data.first_name}
              onChange={handleChange}
              error={Boolean(errors.first_name)}
              helperText={errors.first_name}
              sx={authField}
            />
          </Box>
          <Box>
            <AuthLabel>Last name</AuthLabel>
            <TextField
              fullWidth
              name="last_name"
              value={data.last_name}
              onChange={handleChange}
              error={Boolean(errors.last_name)}
              helperText={errors.last_name}
              sx={authField}
            />
          </Box>
        </Box>

        <AuthLabel>Work email</AuthLabel>
        <TextField
          fullWidth
          name="email"
          type="email"
          placeholder="you@company.com"
          value={data.email}
          onChange={handleChange}
          error={Boolean(errors.email)}
          helperText={errors.email}
          sx={{ ...authField, mb: 1.75 }}
        />

        <AuthLabel>Phone (optional)</AuthLabel>
        <TextField
          fullWidth
          name="phone"
          value={data.phone}
          onChange={handleChange}
          error={Boolean(errors.phone)}
          helperText={errors.phone}
          sx={{ ...authField, mb: 1.75 }}
        />

        <AuthLabel>Password</AuthLabel>
        <TextField
          fullWidth
          name="password"
          type={showPassword ? "text" : "password"}
          placeholder="••••••••"
          value={data.password}
          onChange={handleChange}
          error={Boolean(errors.password)}
          helperText={errors.password}
          sx={{ ...authField, mb: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <IconEyeOff size={16} stroke={1.75} color={color.textMuted} />
                  ) : (
                    <IconEye size={16} stroke={1.75} color={color.textMuted} />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {data.password && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
            <Box
              sx={{
                flex: 1,
                height: 4,
                borderRadius: `${radius.chip}px`,
                bgcolor: color.borderRow,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: 4,
                  width: `${((strength + 1) / 5) * 100}%`,
                  bgcolor: meter.color,
                  transition: "width 120ms ease",
                }}
              />
            </Box>
            <Box sx={{ font: `400 11px ${font.mono}`, color: meter.color }}>{meter.label}</Box>
          </Box>
        )}

        {errors.form && (
          <Box sx={{ mb: 1.5, font: `400 11.5px ${font.sans}`, color: color.danger }}>
            {errors.form}
          </Box>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{ py: 1.5, fontSize: 13.5, mt: data.password ? 0 : 1.25 }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Create account →"}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default SignUp;
